import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import logoIcon from "@/assets/logo-icon.webp";

const navItems = [
  { label: "Why", href: "/#why" },
  { label: "Use Cases", href: "/#use-cases" },
  { label: "Resources", href: "/#resources" },
  { label: "Blog", href: "/blog" },
  { label: "FAQ", href: "/#faq" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLAnchorElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [offsets, setOffsets] = useState({ logo: 0, menu: 0 });

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 125);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    const calculate = () => {
      const container = containerRef.current;
      const logo = logoRef.current;
      const menu = menuRef.current;
      if (!container || !logo || !menu) return;

      const containerRect = container.getBoundingClientRect();
      const logoRect = logo.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();

      const containerCenter = containerRect.left + containerRect.width / 2;
      const totalWidth = logoRect.width + 100 + menuRect.width;
      const centeredLogoLeft = containerCenter - totalWidth / 2;
      const centeredMenuRight = containerCenter + totalWidth / 2;

      const logoOffset = centeredLogoLeft - logoRect.left;
      const menuOffset = -(menuRect.right - centeredMenuRight);

      setOffsets({ logo: logoOffset, menu: menuOffset });
    };

    calculate();
    window.addEventListener("resize", calculate);
    return () => window.removeEventListener("resize", calculate);
  }, []);

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const el = document.getElementById(location.hash.slice(1));
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [location]);

  const handleNavClick = (href: string) => {
    setOpen(false);
    if (location.pathname === "/" && href.startsWith("/#")) {
      const el = document.getElementById(href.slice(2));
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const shouldCenterNav = scrolled && typeof window !== "undefined" && window.innerWidth >= 768;

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-md focus:text-sm focus:font-semibold"
      >
        Skip to content
      </a>

      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-card/90 backdrop-blur-lg border-b border-border shadow-sm" : "bg-transparent"
        }`}
        aria-label="Main navigation"
      >
        <div ref={containerRef} className="container relative flex items-center justify-between h-16 my-[10px]">
          <Link
            ref={logoRef}
            to="/"
            className="flex items-center h-16 py-1 md:transition-transform md:duration-700 md:ease-in-out md:will-change-transform"
            style={{
              transform: shouldCenterNav ? `translateX(${offsets.logo}px)` : "translateX(0)",
            }}
          >
            <img src={logoIcon} alt="CitizenDevelopers Logo" width={88} height={88} className="h-16 w-auto object-contain flex-shrink-0" />
          </Link>

          <div
            ref={menuRef}
            className="hidden md:flex items-center gap-8 ml-8 transition-transform duration-700 ease-in-out will-change-transform"
            style={{
              transform: shouldCenterNav ? `translateX(${offsets.menu}px)` : "translateX(0)",
            }}
          >
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                onClick={() => handleNavClick(item.href)}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {item.label}
              </Link>
            ))}
            <Button size="sm" className="rounded-full" asChild>
              <Link to="/#newsletter" onClick={() => handleNavClick("/#newsletter")}>Subscribe</Link>
            </Button>
          </div>

          <button
            className="md:hidden text-foreground"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* CSS-only mobile menu transition instead of framer-motion */}
        <div
          className={`md:hidden overflow-hidden border-t border-border bg-card transition-all duration-300 ease-in-out ${
            open ? "max-h-96 opacity-100" : "max-h-0 opacity-0 border-t-0"
          }`}
        >
          <div className="container py-4 flex flex-col gap-3">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className="text-sm text-muted-foreground hover:text-foreground py-2"
                onClick={() => handleNavClick(item.href)}
              >
                {item.label}
              </Link>
            ))}
            <Button size="sm" className="w-fit rounded-full" asChild>
              <Link to="/#newsletter" onClick={() => handleNavClick("/#newsletter")}>Subscribe</Link>
            </Button>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
